import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormBuilder,
} from '@angular/forms';
import { CeIconComponent } from '@enginuity/core/atoms/ce-icon/ce-icon.component';
import { BadgesComponent } from '../badges/badges.component';
import { ActionBtnComponent } from '../action-btn/action-btn.component';
import { DropdownSinglePieceComponent } from '../dropdown-single-piece/dropdown-single-piece.component';
import { WarehouseDropdownComponent } from '../../organisms/warehouse-dropdown/warehouse-dropdown.component';
import { PackageDropdownComponent } from '../../organisms/package-dropdown/package-dropdown.component';
import { SearchComponent } from '../search/search.component';
import { TagsComponent } from '../tags/tags.component';
import { TooltipComponent } from '../tooltip/tooltip.component';

import { LinkBtnComponent } from '../tertiary/link-btn.component';
import { removeElementByIndex } from './utils/removeElementByIndex';
import { InputsValue } from '@services/ui-services/models/inputs-value';
import { WarehouseData } from '@services/shipiment-services/models/warehouse.data';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  standalone: true,
  selector: 'app-inputs',
  templateUrl: './inputs.component.html',
  styleUrls: ['./inputs.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputsComponent),
    },
  ],
  imports: [
    CommonModule,
    CeIconComponent,
    FormsModule,
    ReactiveFormsModule,
    BadgesComponent,
    ActionBtnComponent,
    DropdownSinglePieceComponent,
    WarehouseDropdownComponent,
    PackageDropdownComponent,
    SearchComponent,
    TagsComponent,
    TooltipComponent,

    LinkBtnComponent,
    MatTooltipModule,
  ],
})
export class InputsComponent implements ControlValueAccessor, OnChanges, OnInit {
  @ViewChild('searchInput') searchInputRef!: ElementRef<HTMLInputElement>;
  @Output() onChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() createCustom = new EventEmitter<string>();
  @Output() onEnter = new EventEmitter<string>();
  selecteditems: string = 'Selected warehouse...';
  selectedpackage: string = 'Selected package...';
  isInputFocused: boolean = false;
  private onValueChange: (value: any) => void = (value: any) => {
    this.onChange.emit(value);
  };

  private onTouched = () => {};

  @Input() showOptions: boolean = false;
  @Input() selectedOptions: any[] = [];
  @Input() multiOption: any = null;
  @Input() warehouse: boolean = false;
  @Input() package: boolean = false;
  @Input() footer: boolean = false;
  @Input() moreInfo: boolean = false;
  @Input() info: boolean = false;
  @Input() isMultiOption: boolean = false;
  @Input() multiSelectTags: boolean = false;
  @Input() searchOption: boolean = false;
  @Input() tooltipContent: string = '';
  @Input() conditionLabel: string = '';
  @Input() description: string = '';
  @Input() createOptionLabel: string = 'Create a shipping zone';
  @Input() createNewOptions: boolean = false;
  @Input() optionPlaceholder: string = '';
  @Input() searchMode: boolean = false;
  @Input() disableSearchOption: boolean = false;
  @Input() nestedDropdown: boolean = false;
  @Input() isMultiSelect: boolean = false;
  @Input() disabled: boolean = false;

  toggleNav: boolean = true;

  onInputFocus() {
    this.isInputFocused = true;
  }

  searchQuery: string = '';
  onSearch(search: string) {
    this.searchQuery = search.toLowerCase().replace(/\s+/g, ''); // Convert to lowercase and remove spaces
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent) {
    // Close the options when the Escape key is pressed
    if (this.showOptions) {
      this.showOptions = false;
      if (this.selectedOptions.length === 0 && this.searchQuery === '') {
        this.disableSearchOption = true;
      } else {
        this.disableSearchOption = true;
      }
    }
  }

  @HostListener('document:mousedown', ['$event'])
  onDocumentMouseDown(event: Event) {
    const outside = !this.elementRef.nativeElement.contains(event.target);
    if (outside && this.showOptions) {
      this.showOptions = false;
    }
  }

  @HostListener('document:click', ['$event'])
  clickout(event: Event) {
    // Check if multiSelectTags is true
    if (this.multiSelectTags) {
      if (!this.elementRef.nativeElement.contains(event.target)) {
        this.addTag();
      }
    }
  }

  multiSelectOptionOnEnter(event: any, option: any, controlName: string): void {
    this.multiSelectOption(option, controlName);
  }

  get optionList(): any[] {
    if (this.searchQuery === '') {
      return this.options;
    } else {
      return this.options.filter((option: { label: string }) =>
        option.label.toLowerCase().includes(this.searchQuery.toLowerCase())
      );
    }
  }

  get filteredOptions(): any[] {
    const searchQueryWithoutSpaces = this.searchQuery.replace(/\s+/g, ''); // Remove spaces from search query

    if (searchQueryWithoutSpaces === '') {
      return this.multiOptions;
    } else {
      if (this.nestedDropdown) {
        return this.nestedSearchOptions(this.multiOptions, searchQueryWithoutSpaces);
      }

      const filtered = this.multiOptions.filter(
        (option: { label: string; icon: string }) =>
          option.label.toLowerCase().replace(/\s+/g, '').includes(searchQueryWithoutSpaces) // Remove spaces from option label for comparison
      );
      console.log('Filtered Options:', filtered);
      return filtered;
    }
  }

  nestedSearchOptions(options: any, query: string) {
    let results: any[] = [];

    options.forEach((option: any) => {
      if (option.label.toLowerCase().replace(/\s+/g, '').includes(query.toLowerCase())) {
        results.push(option);
      }
      if (option.children.length > 0) {
        const childResults = this.nestedSearchOptions(option.children, query);
        results = results.concat(childResults);
      }
    });

    return results;
  }

  onInputBlur() {
    this.isInputFocused = false;
  }

  writeValue(value: InputsValue): void {
    this.value = value;
    this.selectForValue(value);
  }

  private selectForValue(value: InputsValue) {
    if (this.multiSelectTags && value) {
      this.tags = Array.isArray(value) ? value : [value];
      // this.addTag();
    }

    if (this.multiSelect) {
      this.selectedOptions =
        this.multiOptions?.filter((option: any) => value.includes(option.value)) ?? [];
    }

    if (this.dropdown) {
      this.selectedOption = { value: value, label: value };
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    const dropdown = changes['dropdown']?.currentValue;
    if (dropdown && this.value && typeof this.value === 'string') {
      this.selectForValue(this.value);
    }
  }

  registerOnChange(fn: any): void {
    this.onValueChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Implement if needed
  }

  // updateValue(newValue: InputsValue): void {
  //   this.value = newValue;
  //   if (!this.multiSelectTags) {
  //     this.onValueChange(this.value);
  //   }
  //   this.onChange.emit(this.value);
  // }
  updateValue(newValue: InputsValue): void {
    this.value = newValue;
    this.onValueChange(this.value);
  }

  onBlur(): void {
    this.onTouched();
    this.onEnter.emit(this.form?.controls['name']?.value);
  }

  toggleOptions() {
    this.showOptions = !this.showOptions;
  }

  toggleSearchMode() {
    this.searchMode = true;
    this.disableSearchOption = false;
    if (this.searchMode) {
      this.searchQuery = '';
      this.showOptions = true;
      this.searchInputRef.nativeElement.focus();
    }
  }
  applySearch() {
    // You can add any additional logic here when pressing Enter in search mode
    this.searchMode = false;
    // Update the value with selected options when applying search
    this.updateValue(this.selectedOptions.map(option => option.value));
  }

  cancelSearch() {
    this.searchMode = false;
  }

  selectOption(option: string, controlName: any) {
    this.form.controls[controlName].setValue(option);
    this.selectedOption = option;
    this.showOptions = false;
    this.updateValue(option);
    this.onChange.emit(option);
  }

  multiSelectOption(option: any, controlName: string) {
    this.isMultiOption = true;
    this.disableSearchOption = true;
    const index = this.selectedOptions.findIndex(
      selectedOption => selectedOption.value === option.value
    );
    if (index !== -1) {
      // this.selectedOptions.splice(index, 1);
      // this.addOptionToMultiOptions(option);
    } else {
      this.selectedOptions.push(option);
      // this.removeOptionFromMultiOptions(option);
    }
    this.updateValue(this.selectedOptions.map(option => option.value));
    console.log(this.value);
  }

  nestedOptionSelect(option: any, controlName: string) {
    if (this.isMultiSelect) {
      const index = this.selectedOptions.findIndex(
        selectedOption => selectedOption.value === option.value
      );

      if (index !== -1) {
        this.selectedOptions.splice(index, 1);
        //this.addOptionToMultiOptions(option);
      } else {
        if (this.selectedOptions.length <= 5) {
          this.selectedOptions.push(option);
          this.removeOptionFromMultiOptions(option);
        }
      }
      this.updateValue(this.selectedOptions.map(option => option.value));
    } else {
      this.selectedOptions = [option];
      this.updateValue(option.value);
    }
  }

  removeOptionFromMultiOptions(option: any) {
    const index = this.multiOptions.findIndex(
      (multiOption: { value: any }) => multiOption.value === option.value
    );
    if (index !== -1) {
      this.multiOptions.splice(index, 1);
    }
  }

  dismissOption(label: string) {
    const index = this.selectedOptions.findIndex(
      option => option.label === label || option.tagLabel === label
    );
    if (index !== -1) {
      const dismissedOption = this.selectedOptions.splice(index, 1)[0];
      this.onValueChange(this.selectedOptions.map(option => option.value));
      // this.addOptionToMultiOptions(dismissedOption);
      this.disableSearchOption = true;
      if (this.selectedOptions.length === 0) {
        this.isMultiOption = false;
        this.disableSearchOption = false;
      }
    }
  }

  // addOptionToMultiOptions(option: any) {
  //   this.multiOptions.push(option);
  // }

  updatedValue(controlName: string) {
    this.form.controls[controlName].setValue(this.selectedOptions);
    this.isMultiOption = false;
  }

  @Input()
  multiOptions: any = [
    { value: 'option1', label: 'Option 1', icon: 'list', showIcon: false },
    { value: 'option2', label: 'Option 2', icon: 'list', showIcon: false },
    { value: 'option3', label: 'Option 3', icon: 'list', showIcon: false },
  ];
  getIconClass(iconType: string): string {
    return iconType; // Assuming that the iconType property holds the class names for Font Awesome icons
  }
  selectionChanged(selectedValue: string) {
    // console.log(selectedValue)
    this.selecteditems = selectedValue;
    this.selectedpackage = selectedValue;
    this.selectedOption = selectedValue;
    this.selectedOptions = selectedValue ? [selectedValue] : [];
    if (selectedValue) {
      this.showOptions = false;
    }
    this.onChange.emit(selectedValue);
  }

  @Input() selectedOption: any = '';

  @Input()
  options: any = [
    { value: 'option1', label: 'Option 1' },
    { value: 'option2', label: 'Option 2' },
    { value: 'option3', label: 'Option 3' },
  ];

  @Input()
  warehouses: WarehouseData[] = [];

  @Input()
  packages: any = [
    {
      icon: 'settings',
      label: 'Custom package',
      tagLine: '',
      showButton: false,
    },
    {
      icon: 'subscription',
      label: 'Automatic',
      tagLine: 'You need to set up boxes to use this feature',
      showButton: true,
    },
  ];

  @Input() Label = 'Label';

  @Input() type = 'text';

  @Input() placeholder = '';

  @Input() tagLine: string = '';

  @Input() addTags: string = '';
  @Input() tags: any[] = [];

  @Input() toggle = 'dropdown-toggle';
  @Input() active: boolean = false;

  @Input() icon: boolean = false;

  @Input() dropdown: boolean = false;
  @Input() multiSelect: boolean = false;
  @Input() colorPicker: boolean = false;
  colorchange: string = '';
  @Input() stepCounter: boolean = false;

  @Input() prefix: boolean = false;
  @Input() suffix: boolean = false;
  @Input() prefixBadge: any = {
    icon: 'down_arrow',
    type: 'white',
    label: '%',
    rightIcon: true,
  };
  @Input() suffixBadge: any = {
    icon: 'down_arrow',
    type: 'white',
    label: 'units',
    rightIcon: true,
  };

  @Input() prefixDropdown = [
    {
      name: '%',
    },
    {
      name: '$',
    },
  ];

  @Input() suffixDropdown = [
    {
      name: 'units',
    },
    {
      name: 'times',
    },
  ];
  @Input()
  warn = false;

  @Input()
  name = 'name';

  @Input()
  state: 'default' | 'inactive' | 'success' | 'warning' | 'focused' = 'default';

  @Input()
  error: string | null = 'error message';

  _value: InputsValue = '';

  get value(): InputsValue {
    return this._value;
  }

  @Input()
  set value(v: InputsValue) {
    this._value = v;
    if (!this.multiSelectTags) {
      this.form.patchValue({ name: v }, { onlySelf: true });
    }
    if (this.multiSelect) {
      console.log('SSS');
    }
    if (v && typeof v === 'string') {
      this.selectForValue(v);
    }
  }

  @Input() form!: FormGroup;

  cues = false;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private readonly elementRef: ElementRef
  ) {
    this.form = this._formBuilder.group({
      name: [this.value],
    });
  }

  ngOnInit(): void {}

  onStep(action: string): void {
    const control = this.form.get(this.name);
    if (control) {
      const currentValue = control.value || 0;
      if (action === 'increase') {
        control.setValue(parseInt(currentValue) + 1);
      } else if (action === 'decrease') {
        control.setValue(parseInt(currentValue) - 1);
      }
      this.onChange.emit(control.value);
    }
  }

  addTag() {
    const value = this.form.get('name')?.value || '';
    if (!value) return;

    const enteredTags = value.split(/[\n,]/).map((tag: string) => tag.trim()); // Split by both Enter and commas
    const newTags = enteredTags.filter((tag: string) => !!tag); // Filter out any empty tags

    if (newTags.length > 0) {
      this.tags = [...this.tags, ...newTags]; // Push the new tags into the tags array
      this.updateValue(this.tags);
      this.addTags = ''; // Reset the entered value
      this.value = '';
      this.form.get('name')?.setValue('');
    }
  }

  // addTag() {
  //   const enteredTags = this.addTags.split(/[\n,]/).map(tag => tag.trim()); // Split by both Enter and commas
  //   const newTags = enteredTags.filter(tag => !!tag); // Filter out any empty tags

  //   if (newTags.length > 0) {
  //     this.tags = [...this.tags, ...newTags]; // Push the new tags into the tags array
  //     this.addTags = ''; // Reset the entered value
  //     this.onChange.emit(this.tags); // Emit the updated tags array
  //   }
  // }

  dismissTags(index: number) {
    this.tags = removeElementByIndex(this.tags, index); // Remove the tag at the specified index (immutable)
    this.updateValue(this.tags);
  }

  showCues(shown = false) {
    this.cues = shown;
  }
}
